import React, {useEffect, useState} from 'react';
import {Button, Input, message, Modal, Table} from 'antd';
import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {Pageable, PagePrefabDto, PrefabControllerApi, PrefabDto} from "../api";
import {Link} from "react-router-dom";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {getErrorMessage} from "../utils/Utils";


const PrefabOverview = ({system}) => {
    const {keycloak} = useKeycloak();
    const [prefabDto, setPrefabDto] = useState<PrefabDto[]>();
    const [searchText, setSearchText] = useState<string>(''); // Der Suchbegriff
    const [modalVisible, setModalVisible] = useState(false);
    const [deletePrefab, setDeletePrefab] = useState<PrefabDto>();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);

    const headers = useKeycloakHeader();
    const prefabControllerApi = new PrefabControllerApi();

    const handleDeleteButton = (prefab: PrefabDto) => {
        setDeletePrefab(prefab);
        setModalVisible(true);
    }

    const onModalCancel = () => {
        setModalVisible(false);
    }

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'System',
            dataIndex: ['system', 'name'],
            key: 'system.name'
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Bearbeiten',
            key: 'edit',
            render: (text, record) => (
                <div>
                    {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER, ROLES.GLOW_TEST_DESIGNER], keycloak) ? (
                        <Link to={process.env.PUBLIC_URL + `/edit/prefab/${record.id}`}>
                            <EditOutlined style={{fontSize: '16px', color: '#1890ff'}}/>
                        </Link>
                    ) : (
                        <EditOutlined style={{fontSize: '16px', color: '#f0f0f0'}}/>
                    )}
                    {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER], keycloak) ? (
                        <Button
                            danger
                            onClick={() => handleDeleteButton(record)}
                            style={{marginRight: '8px', border: '0px'}}
                        >
                            <DeleteOutlined/>
                        </Button>
                    ) : (
                        <Button disabled style={{marginRight: '8px', border: '0px'}}>
                            <DeleteOutlined/>
                        </Button>
                    )}
                </div>
            ),
        }
    ];

    const fetchData = (page: number, size: number) => {
        const pageable: Pageable = {
            page: page - 1, // Spring verwendet 0-basierte Seitenzahlen
            size: size ? size : 10,
            sort: ['id;desc'] // Beispiel für Sortierung als Array
        } as Pageable;

        prefabControllerApi.getAllPageablePrefabsBySystem(system,
            pageable.size ? pageable.size : 10,
            pageable.page ? pageable.page : 0,
            pageable.sort ? pageable.sort : ['id;asc'],
            headers)
            .then((axiosResponse) => {
                let pagePrefabDto: PagePrefabDto = axiosResponse.data
                if (pagePrefabDto.content) {
                    const axiosData: Array<PrefabDto> = pagePrefabDto.content;
                    setPrefabDto(axiosData);
                    if (pagePrefabDto.totalElements) {
                        setTotalItems(pagePrefabDto.totalElements);
                    }
                }
            })
            .catch((error) => {
                let msg = "Fehler beim Abrufen der Daten: ";
                message.error(msg + getErrorMessage(error));
            });
    }

    useEffect(() => {
        fetchData(currentPage, pageSize);
    }, [system]);

    useEffect(() => {
        fetchData(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };


    // Funktion zum Filtern der Tabelle basierend auf dem Suchbegriff
    const filteredData = prefabDto?.filter((test) =>
        test.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleDeletePrefab = () => {
        if (deletePrefab && deletePrefab.id) {
            prefabControllerApi.deletePrefab(deletePrefab.id, headers).then(response => {
                    // Erfolgreiche Antwort
                    message.success(deletePrefab.name + ' wurde erfolgreich gelöscht!');
                    setModalVisible(false);
                    fetchData(currentPage, pageSize);
                }
            ).catch((error) => {
                message.error(error.response.data);
            })
        }

    }

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }
    return (
        <div>
            <h1>Komponenten</h1>
            <Input
                placeholder="Nach Namen suchen"
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
            />
            <Table
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    locale: {
                        items_per_page: "/ Seite"
                    }
                }}
                locale={{ emptyText: 'Keine Daten vorhanden' }}
                onChange={handleTableChange}
                dataSource={filteredData} columns={columns}/>
            <Modal
                open={modalVisible}
                title={"Bist du dir sicher?"}
                onCancel={onModalCancel}
                footer={[
                    <Button key="cancel" onClick={onModalCancel}>
                        Nein
                    </Button>,
                    <Button key="save" danger onClick={handleDeletePrefab}>
                        Ja
                    </Button>,
                ]}
            >
                <span>Willst du wirklich löschen: <b>{deletePrefab?.name} ({deletePrefab?.id})</b>?</span>
            </Modal>
        </div>
    );
};

export default PrefabOverview;